<template lang="pug">
  .package-select-wrapper(v-if="packagesQty > 1")
    RogueButton.package-select(
      @click="toggleAvailablePackages"
      size="s"
      right-icon="arrow-down"
    )
     span(v-html=`__('<span class="active-package">{currentPackageNumber}</span> of {totalPackageCount}', {currentPackageNumber: currentPackageText, totalPackageCount:packagesQty})`)
    .package-select-overlay(
      @click="toggleAvailablePackages"
      v-show="showAvailablePackages"
    )
    Transition(name="openUp")
      .available-packages(v-show="showAvailablePackages")
        .package-wrapper(
          v-for="(pkg, index) in packages"
          :key="index"
          :class="{'is-divider': index !== 0}"
        )
          .package(
            @click="changePackage(index)"
            :class="{ 'active': index === currentPackageText - 1 }"
          )
            span.package-number(
              v-html=`__('<span class="active-package">{currentPackageNumber}</span> of {totalPackageCount}', {currentPackageNumber: index + 1, totalPackageCount:packagesQty})`
            )
            span.package-status
              span.status-and-icon
                Icon.icon-size-m(
                  :class="findCurrentStatusIcon(pkg).replace('/', '-')"
                  :name="findCurrentStatusIcon(pkg)"
                )
                h4.status {{ pkg.status }}
              span {{ findCurrentStatusDate(pkg) }}
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { shippingStatuses } from '../helpers'
import type { IRssPackage } from '../rssTypes'

const props = defineProps<{
  packages: IRssPackage[] | null,
}>()

const route = useRoute()
const router = useRouter()

const currentPackage = ref<number>(0)
const showAvailablePackages = ref<boolean>(false)

// Return package quantity from rss data packages
const packagesQty = computed<number>(() => props.packages?.length || 0)

// Get current package text
const currentPackageText = computed<number | string>(() => currentPackage.value + 1 || '-')

/**
 * Toggle show available packages
 * @return {void}
 */
function toggleAvailablePackages(): void {
  showAvailablePackages.value = !showAvailablePackages.value
}

/**
 * Select the first non-delivered package as default.
 * @return {void}
 */
function selectDefaultPackage(): void {
  const urlPackage = route.query.packageId
  const pkgIndex = Number(urlPackage) - 1

  if (urlPackage && props.packages?.[pkgIndex]) {
    changePackage(pkgIndex)
  } else if (props.packages) {
    const firstActiveIndex = props.packages.findIndex(pkg => pkg.status !== 'delivered')
    if (firstActiveIndex !== -1) {
      changePackage(firstActiveIndex)
    }
  }
}

/**
 * Emit to set the current package to the sent one,
 * and hide the available packages modal
 * @param  {Number} packageIndex
 * @return {void}
 */
async function changePackage(packageIndex: number): Promise<void> {
  if (packageIndex !== currentPackage.value) {
    currentPackage.value = packageIndex
    await router
      .replace({ query: { ...route.query, packageId: packageIndex + 1 } })
      // Prevents the `NavigationDuplicated` when page reloads
      .catch(() => {})
  }
  showAvailablePackages.value = false
}

/**
 * Accept sent package
 * Determine which date data to return if any
 * @param  {Object} sentPackage
 * @return {String}
 */
function findCurrentStatusDate(sentPackage: IRssPackage): string {
  const currentStep = (shippingStatuses as any)[sentPackage.status!]
  const options: Intl.DateTimeFormatOptions = {
    month  : 'short',
    day    : 'numeric',
    hour   : 'numeric',
    minute : 'numeric',
  }

  if (currentStep && Object.prototype.hasOwnProperty.call(sentPackage, currentStep.dateKey)) {
    const dateValue = sentPackage[currentStep.dateKey as keyof IRssPackage]

    return new Date(dateValue as string).toLocaleDateString(undefined, options)
  }

  return ''
}

/**
 * Determine which icon to return if any
 * @param  {Object} sentPackage
 * @return {String}
 */
function findCurrentStatusIcon(sentPackage: IRssPackage): string {
  const currentStep = (shippingStatuses as any)[sentPackage.status!]

  if (currentStep && Object.prototype.hasOwnProperty.call(currentStep, 'icon')) {
    return currentStep.icon
  }

  return ''
}

onMounted(() => {
  selectDefaultPackage()
})
</script>

<style lang="scss" scoped>
  .package-select-wrapper {
    position : relative;

    .active-package {
      display       : inline-block;
      height        : $space-m;
      width         : $space-m;
      line-height   : $space-m;
      color         : $color-white;
      background    : $color-black;
      border-radius : 100%;
      text-align    : center;
    }

    .package-select {
      margin-bottom : 0;
      white-space   : nowrap;
      padding       : $space-xxs $space-xs;
    }

    .icon-arrow-down {
      margin-left : $space-xxxs;
      height      : $size-xs;
      width       : $size-xs;
      display     : inline-block;
    }

    .package-select-overlay {
      position : fixed;
      top      : 0;
      left     : 0;
      width    : 100%;
      height   : 100%;
      z-index  : 9;
    }

    .available-packages {
      position      : absolute;
      top           : 0;
      right         : 0;
      width         : calc(100vw - (#{$space-m * 2} + #{$space-s * 2}));
      background    : $color-white;
      border        : $border-width-thin solid $color-gray-light;
      border-radius : $radius-default;
      z-index       : 10;
      box-shadow    : $shadow-s;

      .package-wrapper {
        &.is-divider {
          border-top : $border-width-thin solid $color-gray-light;
        }

        .package {
          display     : flex;
          align-items : center;
          padding     : $space-xxs $space-xs;
          margin      : $space-xxxs;
          cursor      : pointer;
          font        : $size-xs $font-condensed;

          &:hover, &.active {
            background : $color-gray-light;
          }

          &-number {
            position     : relative;
            margin-right : $space-l;
            white-space  : nowrap;

            &:after {
              content    : '';
              position   : absolute;
              right      : -$space-xs;
              top        : -$space-xs;
              width      : 1px;
              height     : calc(100% + #{$space-l});
              background : $color-gray-light;
            }
          }

          &-status {
            display         : flex;
            justify-content : space-between;
            align-items     : center;
            width           : 100%;

            .status-and-icon {
              padding-left   : $space-xxs;
              display        : flex;
              flex-direction : row;
              align-items    : center;

              .status {
                margin    : 0 $space-xxs;
                font-size : $size-xs;
              }
            }
          }

          @media(min-width : $mobile) {
            &-number {
              &:after {
                top    : -$space-xxs;
                height : calc(100% + #{$space-s});
              }
            }

            &-status {
              flex-flow : row;

              .status-and-icon {
                padding-left : 0;
              }
            }
          }
        }
      }

      @media(min-width : $small-desktop) {
        width : auto;

        .package-wrapper .package {
          white-space : nowrap;
        }
      }
    }
  }

  //animation
  .openUp {
    &-enter-active {
      transition : all 100ms ease-out
    }

    &-leave-active {
      transition : all 50ms ease-in;
      position   : absolute;
    }

    &-enter-from,
    &-leave-to {
      opacity : 0
    }

    &-enter-from {
      transform : scale(0.9)
    }
  }
</style>
